import { Money, dateCell } from '../items/ItemList';
import React, {Component} from 'react';
import { ButtonGroup } from 'reactstrap';
import { NavLink } from 'react-router-dom';
import DeleteInvoice from './containers/DeleteInvoice';
import { INVOICE_TYPE } from './containers/InvoiceList';
import {connect} from 'react-redux';
import { BaseUrl } from '../BaseUrl/BaseUrl';
import { sendMessage } from 'apa/actions';
import Dialog from '../apa/containers/Dialog';
import autobind from 'autobind-decorator';

// Helper function to check if the invoice is overdue by more than 4 weeks
const isOverdue = (dueDate) => {
  const today = new Date();
  const dueDateObj = new Date(dueDate);
  const timeDiff = today - dueDateObj;
  const weeksInMs = 4 * 7 * 24 * 60 * 60 * 1000; // 4 weeks in milliseconds
  return timeDiff > weeksInMs;
};

@connect(
    state => {
        return {
            token: state.apa.token
        }
    },
    dispatch => {
        return {
            sendMessage: (message, type) => dispatch(sendMessage({text: message, color: type}))
        }
    }
)

// Invoice actions (edit, download, delete)
class InvoiceActions extends Component {
    constructor(props){
        super(props);
        this.state = {
            showTooltip: false,
            isSending: false,
            is_sent: props.row.is_emailed,
            sent_at: props.row.emailed_at,
            isModalOpen: false
        };
    }

    @autobind
    toggleModal() {
        this.setState(
            {
                ...this.state,
                isModalOpen: !this.state.isModalOpen
            }
        );
    }

    @autobind
    confirm() {
        this.setState({ isSending: true });
        this.toggleModal();
        fetch(`${BaseUrl}/api/invoices/${this.props.row.id}/email-invoice/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${this.props.token}`,
          },
        })
          .then((response) => response.json())
          .then((data) => {
            this.setState({ 
                isSending: false,
                is_sent: true,
                sent_at: data.emailed_at
            })
            this.props.sendMessage('Rechnung erfolgreich gesendet', 'success')
        })
          .catch((error) => {
            this.setState({isSending: false})
            console.error("Error:", error)
            this.props.sendMessage('Fehler aufgetreten.', 'danger');
        });
    }

    render(){
        const { row } = this.props;

        return (
            <ButtonGroup>
                <div
                    style={{ position: "relative", display: "inline-block" }}
                    onMouseEnter={() => this.setState({showTooltip: true})}
                    onMouseLeave={() => this.setState({showTooltip: false})}
                >
                    <button
                        onClick={this.toggleModal}
                        className={`btn ${this.state.is_sent ? "btn-success" : "btn-primary"}`}
                        disabled={!row.customer.invoice_recipient || this.state.is_sent || this.state.isSending}
                    >
                        <i className="fa fa-envelope"> </i>
                    </button>
                    {
                        this.state.showTooltip && (
                            <div
                                style={{
                                    position: "absolute",
                                    bottom: "110%",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    backgroundColor: "black",
                                    color: "white",
                                    padding: "5px 10px",
                                    borderRadius: "5px",
                                    fontSize: "12px",
                                    whiteSpace: "nowrap",
                                    zIndex: 10,
                                }}
                            >
                                {
                                    this.state.is_sent && this.state.sent_at ? 
                                    new Date(this.state.sent_at).toLocaleDateString("de-DE", {
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric"
                                    }) : !row.customer.invoice_recipient ? 
                                    "Bitte geben Sie die E-Mail-Adresse des Rechnungsempfängers an, um diese Aktion auszuführen." : 
                                    "Diese Rechnung per E-Mail an die hinterlegte Adresse versenden."
                                }
                            </div>
                        )
                    }
                    <Dialog
                        toggle={this.toggleModal}
                        confirm={this.confirm}
                        isOpen={this.state.isModalOpen}
                        title="Rechnung versenden"
                    >
                        Soll die Rechnung wirklich versendet werden?
                    </Dialog>
                </div>            
                <NavLink to={`/invoices/${row.id}`} className='btn btn-primary'>
                    <i className='fa fa-edit'> </i>
                </NavLink>
                <a href={row.url} className='btn btn-primary'>
                    <i className={'fa fa-download'}> </i>
                </a>
                <DeleteInvoice invoice={row} />
            </ButtonGroup>
        );
    }
}

InvoiceActions.displayName = 'InvoiceActions';

// Invoice list columns definition with conditional red styling for overdue unpaid invoices
export const invoiceListColumns = [
    {
        text: 'Rechnungsnummer',
        dataField: 'invoice_nr',
        headerClasses: 'text-left align-middle',
        classes: 'text-left align-middle',
        sort: true,
        formatter: function InvoiceNrFormatter(cell, row) {
            return (
                <span style={{ color: row.status === 0 && isOverdue(row.due_date) ? 'red' : 'inherit' }}>
                    {cell}
                </span>
            );
        }
    },
    {
        text: 'Kunde',
        dataField: 'customer.name',
        headerClasses: 'text-left align-middle',
        classes: 'text-left align-middle',
        sort: true,
        formatter: function CustomerNameFormatter(cell, row) {
            const dueDate = new Date(row.due_date);

            if (isNaN(dueDate.getTime())) {
                return dateCell(row.due_date); // Return formatted date if invalid
            }
            const now = new Date();
            const timeDiff = now - dueDate;
            const weeksDiff = timeDiff / (1000 * 60 * 60 * 24 * 7);
            const isOverdue = weeksDiff > 4 && row.status === 0;
            return (
                <span style={{ color: isOverdue ? 'red' : 'inherit' }}>
                    {cell}
                </span>
            );
        }
    },
    {
        text: 'Rechnungsdatum',
        dataField: 'invoice_date',
        headerClasses: 'text-left align-middle',
        classes: 'text-left align-middle',
        sort: true,
        formatter: function InvoiceDateFormatter(cell, row) {
            return (
                <span style={{ color: row.status === 0 && isOverdue(row.due_date) ? 'red' : 'inherit' }}>
                    {dateCell(cell)}
                </span>
            );
        }
    },
    {
        text: 'Fälligkeitsdatum',
        dataField: 'due_date',
        headerClasses: 'text-left align-middle',
        classes: 'text-left align-middle',
        sort: true,
        formatter: function DueDateFormatter(cell, row) {
            const dueDate = new Date(cell);
            if (isNaN(dueDate.getTime())) {
                return dateCell(cell); // Return formatted date if invalid
            }
            const now = new Date();
            const timeDiff = now - dueDate;
            const weeksDiff = timeDiff / (1000 * 60 * 60 * 24 * 7);
            const isOverdue = weeksDiff > 4 && row.status === 0;
            return (
                <span style={{ color: isOverdue ? 'red' : 'inherit' }}>
                    {dateCell(cell)}
                </span>
            );
        }
    },
    {
        text: 'Status',
        dataField: 'status',
        headerClasses: 'text-left align-middle',
        classes: 'text-left align-middle',
        sort: true,
        formatter: function StatusFormatter(cell, row) {
            return (
                <span style={{ color: row.status === 0 && isOverdue(row.due_date) ? 'red' : 'inherit' }}>
                    {INVOICE_TYPE[cell]}
                </span>
            );
        }
    },
    {
        text: 'Bezahlt am',
        dataField: 'paid_at',
        headerClasses: 'text-left align-middle',
        classes: 'text-left align-middle',
        sort: true,
        formatter: function PaidDateFormatter(cell, row) {
            const paidDate = new Date(cell);
            if (isNaN(paidDate.getTime())) {
                return dateCell(cell);
            }
            const now = new Date();
            const timeDiff = now - paidDate;
            const weeksDiff = timeDiff / (1000 * 60 * 60 * 24 * 7);
            const isOverdue = weeksDiff > 4 && row.status === 0;
            return (
                <span style={{ color: isOverdue ? 'red' : 'inherit' }}>
                    {dateCell(cell)}
                </span>
            );
        }
    },
    {
        text: 'Summe',
        dataField: 'after_tax',
        headerClasses: 'text-right align-middle',
        classes: 'text-right align-middle',
        sort: true,
        formatter: function AmountFormatter(cell, row) {
            return (
                <span style={{ color: row.status === 0 && isOverdue(row.due_date) ? 'red' : 'inherit' }}>
                    {Money(cell)}
                </span>
            );
        }
    },
    {
        dataField: 'id',
        text: '',
        isDummyField: true,
        formatter: (cell, row) => <InvoiceActions row={row} />
    }
];
